import config from '../config/'
import * as Sentry from '@sentry/vue'

class AnalyticsScoresService {
  constructor(msal) {
    this.baseUrl = config.apiUrl
    this.msal = msal
  }

  getAuthHeaders() {
    const token = this.msal?.idToken
    
    if (!token) {
      throw new Error('No authentication token available')
    }
    
    return {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    }
  }

  async loadConfig() {
    try {
      const headers = this.getAuthHeaders()
      
      const response = await fetch(`${this.baseUrl}/GamesFeedsAppInsightsConfig`, {
        method: 'GET',
        headers,
        mode: 'cors'
      })
      
      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Unauthorized - you should provide api access token')
        } else if (response.status === 403) {
          throw new Error('User not authorized for this action')
        } else {
          throw new Error(`HTTP error! status: ${response.status}`)
        }
      }
      
      return await response.json()
    } catch (error) {
      console.error('Error loading analytics config:', error)
      Sentry.captureException(error, { tags: { service: 'AnalyticsScores', op: 'loadConfig' } })
      throw error
    }
  }

  async saveConfig(configData) {
    try {
      const headers = this.getAuthHeaders()
      headers['Content-Type'] = 'application/json-patch+json'
      
      const response = await fetch(`${this.baseUrl}/GamesFeedsAppInsightsConfig`, {
        method: 'POST',
        headers,
        body: JSON.stringify(configData)
      })

      if (!response.ok) {
        if (response.status === 400) {
          throw new Error('Model state not valid - check your configuration data')
        } else if (response.status === 401) {
          throw new Error('Unauthorized - you should provide api access token')
        } else if (response.status === 403) {
          throw new Error('User not authorized for this action')
        } else {
          throw new Error(`HTTP error! status: ${response.status}`)
        }
      }

      // Проверяем, есть ли тело ответа
      const contentLength = response.headers.get('content-length')
      const hasBody = contentLength && parseInt(contentLength) > 0
      
      if (hasBody) {
        return await response.json()
      } else {
        // Если ответ пустой, возвращаем объект успеха
        return { success: true, message: 'Configuration saved successfully' }
      }
    } catch (error) {
      console.error('Error saving analytics config:', error)
      Sentry.captureException(error, { tags: { service: 'AnalyticsScores', op: 'saveConfig' } })
      throw error
    }
  }

  validateConfig(configData) {
    const errors = []

    if (!Array.isArray(configData)) {
      errors.push('Configuration must be an array')
      return errors
    }

    configData.forEach((item, index) => {
      if (!item.description) {
        errors.push(`Item at index ${index} is missing 'description' field`)
      }
      
      if (!item.instrumentationKey) {
        errors.push(`Item at index ${index} is missing 'instrumentationKey' field`)
      }
      
      if (typeof item.defaultSamplingPercentage !== 'number') {
        errors.push(`Item at index ${index} has invalid 'defaultSamplingPercentage' (must be number)`)
      }
      
      if (!Array.isArray(item.selectedDomains)) {
        errors.push(`Item at index ${index} has invalid 'selectedDomains' (must be array)`)
      }
      
      if (typeof item.selectedDomainsSamplingPercentage !== 'number') {
        errors.push(`Item at index ${index} has invalid 'selectedDomainsSamplingPercentage' (must be number)`)
      }
    })

    return errors
  }
}

export default AnalyticsScoresService 